import React, { useEffect, useRef, useState } from 'react';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import Hls from 'hls.js';
import { fetchPlaylistObjectUrl } from './hlsPlaylist';

interface AudioPlayerProps {
    audioId: number;
    autoplay?: boolean;
}

/** What a member is told when there is nothing left to try. Slovak, like every other string a member reads. */
const PLAYBACK_FAILED = 'Nahrávku sa nepodarilo načítať.';

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioId, autoplay = false }) => {
    const playlistPath = `/posts/${audioId}/playlist.m3u8`;

    const [audioSrc, setAudioSrc] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const audioElementRef = useRef<HTMLAudioElement | null>(null);
    const playerRef = useRef<Plyr | null>(null);
    /**
     * The manifest URL that is actually live. It is not the same thing as `audioSrc`: a 403 refetch
     * replaces the manifest on the running Hls instance without going through state, because setting
     * state there would rebuild the player and restart the podcast at 0:00. Whatever is in here is
     * what has to be revoked.
     */
    const objectUrlRef = useRef<string | null>(null);
    const playlistPathRef = useRef(playlistPath);

    /** Read by the player effect, which must not restart when it changes. Declared first so it has
     *  run before the effects below on every commit. */
    useEffect(() => {
        playlistPathRef.current = playlistPath;
    });

    /**
     * The player, and everything that has to be undone when it goes.
     *
     * WHY THIS EFFECT IS DECLARED BEFORE THE FETCH BELOW that feeds it. React runs cleanups in the
     * order the effects were declared, and the Hls instance must be destroyed before the object URL
     * it was built from is revoked — not the reverse. A parsed VOD manifest survives the wrong
     * order today, but a manifest re-read (a live playlist, a retried manifest load) would be
     * reading a blob that no longer exists, and that is not a failure anyone would trace back here.
     */
    useEffect(() => {
        const audio = audioElementRef.current;
        if (!audio || !audioSrc) {
            return;
        }
        // After a 403 refetch the ref holds a newer manifest than state does, and the one in state
        // has been revoked. A rebuild has to use the live one.
        const source = objectUrlRef.current ?? audioSrc;

        /**
         * WHY THE PLAYER IS REUSED RATHER THAN REBUILT, AND WHY IT OUTLIVES THIS EFFECT.
         *
         * This effect re-runs without the component unmounting whenever `audioSrc` or `autoplay`
         * changes. A second `new Plyr(audio)` on that path cannot work: plyr 3.7.8 marks the element
         * at setup (`this.media.plyr = this`, dist:8057) and NOTHING ever clears the marker —
         * `destroy()` only puts the clone back in the container's place (dist:7781). The second
         * construction therefore takes the "Target already setup" bail (dist:7927-7930) and returns
         * an instance that was never set up, whose own `destroy()` then no-ops on `if (!this.ready)`
         * (dist:7743) and orphans the first player. Hls would attach to an element plyr had already
         * moved out of the document, and the member would be left with a dead player.
         *
         * So the player belongs to the component, not to this effect: built once, and destroyed only
         * when the component unmounts (the effect at the bottom of this file). A reused instance
         * keeps the options it was built with, which costs nothing here — it is this component, not
         * plyr, that starts playback — and no call site changes `autoplay` on a mounted player
         * anyway. Handing back a dead one is the alternative.
         */
        const player = playerRef.current ?? new Plyr(audio, {
            autoplay: autoplay,
            controls: ['play', 'progress', 'current-time', 'mute', 'volume', 'settings'],
            muted: autoplay, // Mute the audio if autoplay is enabled to avoid browser restrictions
        });
        playerRef.current = player;

        let hls: Hls | null = null;
        let onLoadedMetadata: (() => void) | null = null;
        let onNativeError: (() => void) | null = null;
        let nativeResumeAt = 0;
        let nativeWasPlaying = false;
        /**
         * How far the one recovery in flight has got. This is the only thing standing between a
         * signature expiry and an endless refetch loop, so it is a state rather than a flag:
         *
         *   'idle'      nothing outstanding — a segment 403 may mint a fresh manifest.
         *   'reminting' a fresh manifest has been asked for, and is not installed yet.
         *   'unproven'  the fresh manifest is installed; nothing has loaded from it yet.
         *
         * WHY IT IS NOT A ONE-SHOT LATCH. It used to be set on the first segment 403 and never
         * cleared again, so exactly one recovery was possible for the life of the mount. Signatures
         * are valid for `application.security.media.url-ttl` (12 h) from the moment the manifest was
         * minted, so a member who leaves the feed open across two of those lifetimes — or who starts
         * a long podcast after a long idle — hits the second expiry, falls straight through to
         * `giveUp`, and gets a permanent error overlay with nothing left to retry. This is the
         * mechanism that has to hold for `require-signed-segments` to be safe to arm for the web, so
         * it must survive more than once.
         *
         * WHY FRAG_LOADED AND NOT MANIFEST_PARSED. Only evidence that the last refetch WORKED may
         * return this to 'idle'. MANIFEST_PARSED proves no more than that a playlist came back and
         * parsed — exactly what a server that then refuses every segment would also do, which is the
         * loop itself. A loaded fragment is the honest signal: a segment was asked for with a
         * freshly minted signature and answered with bytes.
         *
         * WHY 'reminting' IS NOT 'unproven'. hls.js can have another segment request in flight when
         * the 403 lands, and that one was issued against the manifest that just failed. Arriving
         * while the refetch is still going, it says nothing about the fresh manifest — and clearing
         * on it would let the next 403 start a second refetch on top of the first, which is the loop
         * this guard exists to stop. A fragment only counts once `loadSource` has installed what was
         * fetched.
         */
        let recovery: 'idle' | 'reminting' | 'unproven' = 'idle';
        let disposed = false;
        // Initial autoplay is one-shot; each recovery supplies the member's current playing state.
        let playAfterManifest = autoplay;
        let recoveryPlaybackListeners: { onPlay: () => void; onPause: () => void } | null = null;

        const clearRecoveryPlaybackListeners = () => {
            if (recoveryPlaybackListeners) {
                audio.removeEventListener('play', recoveryPlaybackListeners.onPlay);
                audio.removeEventListener('pause', recoveryPlaybackListeners.onPause);
                recoveryPlaybackListeners = null;
            }
        };

        const trackRecoveryPlaybackIntent = (setPlaying: (playing: boolean) => void) => {
            clearRecoveryPlaybackListeners();
            let playedDuringRecovery = false;
            const onPlay = () => {
                playedDuringRecovery = true;
                setPlaying(true);
            };
            const onPause = () => {
                // Ignore the pause caused by replacing the source. Once the member starts
                // playback on the replacement, later pauses reflect their current intent.
                // Pause events can be queued; if the member has resumed by the time one fires,
                // it must not override that newer state.
                if (playedDuringRecovery && audio.paused) {
                    setPlaying(false);
                }
            };
            recoveryPlaybackListeners = { onPlay, onPause };
            audio.addEventListener('play', onPlay);
            audio.addEventListener('pause', onPause);
        };

        const startPlayback = (shouldPlay = autoplay) => {
            if (shouldPlay) {
                audio.play().catch((err) => {
                    console.error('Playback start failed:', err);
                });
            }
        };

        /**
         * The expensive half of the teardown: `new Hls()` runs a TaskLoop on a timer and holds the
         * MSE buffers. Idempotent, because it is reached both from the effect's cleanup and from
         * giving up on playback while the component is still on screen.
         */
        const stopStreaming = () => {
            if (hls) {
                hls.destroy();
                hls = null;
            }
        };

        /**
         * Idempotent, because it is the effect's cleanup and React may call it more than once.
         * Before this, nothing destroyed the Hls instance at all and the teardown sat on the fetch
         * effect, which destroyed the Plyr wrapper while leaving Hls running.
         */
        const teardown = () => {
            if (disposed) {
                return;
            }
            disposed = true;
            clearRecoveryPlaybackListeners();
            if (onLoadedMetadata) {
                audio.removeEventListener('loadedmetadata', onLoadedMetadata);
                onLoadedMetadata = null;
            }
            if (onNativeError) {
                audio.removeEventListener('error', onNativeError);
                onNativeError = null;
            }
            stopStreaming();
        };

        /**
         * Playback is over and the member is told so.
         *
         * It stops the stream but deliberately does NOT destroy the Plyr player, because the
         * component stays mounted: `player.destroy()` puts a clone of the media element where
         * Plyr's container was (plyr 3.7.8 dist:7781), which moves a node React believes it owns.
         * React then reconciles the next render against a DOM that no longer matches it. Plyr is
         * destroyed by `teardown` instead — at unmount, when React has stopped caring what is under
         * that div. See the note above the markup.
         */
        const giveUp = (reason: string, code?: number) => {
            console.error('HLS playback failed:', reason, code ?? '');
            clearRecoveryPlaybackListeners();
            stopStreaming();
            setError(PLAYBACK_FAILED);
        };

        /**
         * A fresh manifest, with signatures minted now, swapped into the running Hls instance.
         *
         * The signatures in a manifest are valid for `application.security.media.url-ttl` (12 h)
         * from the moment it was fetched, and this app fetches it when the feed mounts. A member who
         * leaves the tab open longer than that and then plays a podcast asks for segments whose
         * signature has expired, and once `require-signed-segments` is armed the server answers 403.
         * Before this, that was the silent end of playback.
         */
        const remintManifest = async (resumeAt: number) => {
            try {
                const fresh = await fetchPlaylistObjectUrl(playlistPathRef.current);
                if (disposed || !hls) {
                    URL.revokeObjectURL(fresh);
                    return;
                }
                const stale = objectUrlRef.current;
                objectUrlRef.current = fresh;
                if (stale && stale !== fresh) {
                    URL.revokeObjectURL(stale);
                }
                /*
                 * WHERE THE RESUME POSITION GOES, AND WHY IT IS NOT AN ARGUMENT TO startLoad().
                 *
                 * `loadSource` stops loading, detaches and reattaches the media element and triggers
                 * MANIFEST_LOADING (hls.js 1.5.17 dist:28672). The stream controller answers that by
                 * zeroing `startPosition` and `lastCurrentTime` and dropping its levels
                 * (dist:27724-27731), so a `startLoad(resumeAt)` issued afterwards lands with
                 * `levels === null` and takes the branch that only raises `_forceStartLoad`
                 * (dist:27404) — the argument is thrown away. When the fresh manifest parses, the
                 * level controller starts loading with `config.startPosition` (dist:26436), which is
                 * -1 by default, and the "use lastCurrentTime instead" override (dist:27396) cannot
                 * help, because that was zeroed too. The member would land back at 0:00 — the exact
                 * thing this refetch exists to prevent.
                 *
                 * The config field is the one hls.js reads on the path it actually takes, so the
                 * position goes there, before the load. Loading restarts by itself: `autoStartLoad`
                 * defaults to true, which is what calls startLoad with this value.
                 */
                hls.config.startPosition = resumeAt;
                // Replacing the MediaSource pauses the element. Preserve its latest state just
                // before that reset, including a member pausing while the manifest was fetched.
                playAfterManifest = !audio.paused;
                trackRecoveryPlaybackIntent((playing) => { playAfterManifest = playing; });
                hls.loadSource(fresh);
                // Installed: from here on, a loaded fragment is evidence about THIS manifest and
                // may end the recovery. Before it, fragments belong to the one that just failed.
                recovery = 'unproven';
            } catch (err) {
                giveUp('manifest refetch failed', (err as any)?.response?.status);
            }
        };

        if (Hls.isSupported()) {
            hls = new Hls();
            hls.loadSource(source);
            hls.attachMedia(audio);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                const shouldPlay = playAfterManifest;
                playAfterManifest = false;
                clearRecoveryPlaybackListeners();
                startPlayback(shouldPlay);
            });
            hls.on(Hls.Events.FRAG_LOADED, () => {
                // A segment came back with bytes. If a refetched manifest was waiting to prove
                // itself, it just did: the recovery is over, and the next expiry — hours later, on
                // this same mount — is allowed a recovery of its own. A fragment in any other state
                // is ordinary playback, or a leftover of the manifest that failed.
                if (recovery === 'unproven') {
                    recovery = 'idle';
                }
            });
            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (!data.fatal) {
                    // hls.js retries everything non-fatal by itself; logging each one is noise.
                    return;
                }
                // Keyed on the status code because hls.js cannot read the body: the error envelope
                // the rest of this app dispatches on never reaches it. A 403 on a segment means one
                // thing here — the signature in this manifest has expired.
                const segmentForbidden = data.type === Hls.ErrorTypes.NETWORK_ERROR && data.response?.code === 403;
                // A 403 that arrives while a recovery is outstanding is the loop this guard exists
                // for: the fresh manifest was refused just as fast as the stale one — or has not
                // arrived yet — so refetching again would only burn another round trip.
                if (segmentForbidden && recovery === 'idle') {
                    recovery = 'reminting';
                    // Read the position BEFORE loadSource: it detaches and reattaches the media
                    // element, which puts currentTime back to 0.
                    void remintManifest(audio.currentTime);
                    return;
                }
                giveUp(data.details, data.response?.code);
            });
        } else if (audio.canPlayType('application/vnd.apple.mpegurl')) {
            // Older Safari exposes a segment refusal only as a generic media error. Try one fresh
            // manifest, then wait for loadedmetadata before re-arming recovery; another error first
            // means the replacement failed too and must not start a refetch loop.
            audio.src = source;
            onLoadedMetadata = () => {
                if (recovery === 'unproven') {
                    audio.currentTime = nativeResumeAt;
                    recovery = 'idle';
                    const shouldResume = nativeWasPlaying;
                    clearRecoveryPlaybackListeners();
                    if (shouldResume) {
                        audio.play().catch((err) => {
                            console.error('Playback resume failed:', err);
                        });
                    }
                    return;
                }
                startPlayback();
            };
            onNativeError = () => {
                if (recovery !== 'idle') {
                    giveUp('native media error after manifest refetch');
                    return;
                }
                recovery = 'reminting';
                nativeResumeAt = audio.currentTime;
                nativeWasPlaying = !audio.paused;
                void fetchPlaylistObjectUrl(playlistPathRef.current)
                    .then((fresh) => {
                        if (disposed) {
                            URL.revokeObjectURL(fresh);
                            return;
                        }
                        const stale = objectUrlRef.current;
                        objectUrlRef.current = fresh;
                        // A native src replacement pauses the element. Read intent immediately
                        // before that reset, so a pause while the manifest was fetched is kept.
                        nativeWasPlaying = !audio.paused;
                        trackRecoveryPlaybackIntent((playing) => { nativeWasPlaying = playing; });
                        audio.src = fresh;
                        recovery = 'unproven';
                        if (stale && stale !== fresh) {
                            URL.revokeObjectURL(stale);
                        }
                    })
                    .catch((err) => {
                        giveUp('native manifest refetch failed', (err as any)?.response?.status);
                    });
            };
            audio.addEventListener('loadedmetadata', onLoadedMetadata);
            audio.addEventListener('error', onNativeError);
        }

        return teardown;
    }, [audioSrc, autoplay]);

    useEffect(() => {
        // Whether this run has been thrown away. In the effect rather than in state so the cleanup
        // can actually reach it.
        let cancelled = false;

        const loadPlaylist = async () => {
            try {
                const url = await fetchPlaylistObjectUrl(playlistPath);
                if (cancelled) {
                    // Unmounted while the manifest was still being fetched: nothing will play it.
                    URL.revokeObjectURL(url);
                    return;
                }
                objectUrlRef.current = url;
                setAudioSrc(url);
            } catch (err) {
                // Status and message only. Logging the axios error itself prints error.config,
                // whose headers carry this member's bearer token — into a console anyone can open.
                console.error('Error fetching audio:', (err as any)?.response?.status ?? (err as any)?.message ?? 'unknown error');
                setError(PLAYBACK_FAILED);
            }
        };

        loadPlaylist();

        return () => {
            cancelled = true;
            // Revoking belongs to the fetch, not to the player. The player effect above re-runs on
            // an `autoplay` change with the same manifest still in hand; if it revoked the URL on
            // the way out it would rebuild itself around a dead blob.
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, [playlistPath]);

    /**
     * The player's own lifetime: built by the effect above, destroyed here, once, when the component
     * goes.
     *
     * WHY IT IS NOT PART OF THAT EFFECT'S CLEANUP. That effect re-runs on a new manifest or a new
     * `autoplay` while the component stays mounted, and `player.destroy()` moves DOM nodes React
     * believes it owns: it puts the clone taken at setup where plyr's container was (dist:7781),
     * leaving the element `audioElementRef` points at detached. Destroying there is what made a
     * rebuilt player dead — and it is the same hazard `giveUp` already refuses for the same reason.
     * At unmount React has stopped caring what is under that div, so this is the one safe moment.
     *
     * DECLARED LAST so its cleanup runs last: React runs cleanups in declaration order, and both the
     * Hls instance and the manifest blob must be gone before the player that was playing them is
     * taken apart.
     */
    useEffect(() => () => {
        const player = playerRef.current;
        playerRef.current = null;
        if (player) {
            player.destroy();
        }
    }, []);

    /**
     * WHY THE MEDIA ELEMENT IS RENDERED EVEN WHEN PLAYBACK HAS FAILED.
     *
     * Plyr does not leave the element where React put it. At construction it wraps it in a
     * container of its own (plyr 3.7.8 dist:8062), and at destroy it puts back a clone taken at
     * setup (dist:7949, dist:7781). So from the moment a player exists, the <audio> React believes
     * is a child of the div below is a child of Plyr's container instead. Rendering the error
     * message *in place of* the element therefore made React call removeChild for a node that is
     * not there — `NotFoundError` thrown in the commit phase, which the app's ErrorBoundary catches
     * by replacing the whole routed page with "Niečo sa pokazilo". A member whose connection
     * dropped mid-podcast lost the feed, the navigation and every route under it until they
     * reloaded.
     *
     * So the element stays mounted for the life of the component, in a host div of its own that
     * React never has to add to or remove from, and the message is an overlay next to it. Nothing
     * here may become conditional again.
     */
    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <div>
                <audio ref={audioElementRef} className="plyr" controls />
            </div>
            {error && (
                <div
                    role="alert"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 16,
                        textAlign: 'center',
                        background: 'rgba(0, 0, 0, 0.75)',
                        color: '#fff',
                    }}
                >
                    {error}
                </div>
            )}
        </div>
    );
};

export default AudioPlayer;
